import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import "./style.css";
import { useHistory, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { ShimmerThumbnail } from "react-shimmer-effects";
import { BiRightArrowAlt } from "react-icons/bi";
import {
  HiOutlinePhotograph,
  HiOutlineExclamationCircle,
  HiOutlineMicrophone,
  HiOutlineClipboardList,
  HiOutlineCheckCircle,
} from "react-icons/hi";
import {
  getcardetaol_,
  setcarquestionloading,
} from "../Redux/reduxflow/action";
import InspectionShell from "../components/InspectionShell";
import {
  computeLocalInspectPercent,
  fetchActiveChecklistSections,
  fetchMediaStepStatus,
  fetchProductInspect,
  resolveInspectPercent,
} from "../utils/inspectionProgress";
import {
  answersMapFromInspectionCar,
  answersMapFromReport,
  fetchInspectionCarRow,
  mergeAnswerMaps,
  sectionAnswers,
} from "../utils/checklistAnswers";

const MEDIA_STEPS = [
  {
    key: "damage",
    title: "Add Damage Points",
    text: "Mark damage locations on the vehicle map.",
    path: "/inspection/cardamagepoint",
    icon: HiOutlinePhotograph,
  },
  {
    key: "photos",
    title: "Car Damages",
    text: "Capture and review damage photos.",
    path: "/inspection/apicall",
    icon: HiOutlineExclamationCircle,
  },
  {
    key: "audio",
    title: "Audio Record",
    text: "Record engine or cabin audio notes.",
    path: "/inspection/recordaudio",
    icon: HiOutlineMicrophone,
  },
];

export const CarSteps = (props) => {
  const { getcardetaol_, car_detail_data, setcarquestionloading_ } = props;
  const history = useHistory();
  const location = useLocation();
  const [carsteploading, setcarsteploading] = useState(false);
  const [group, setGroup] = useState(
    () => location.state?.stepsGroup || "media"
  ); // media | checklist
  const [selectedBySection, setSelectedBySection] = useState({});
  const [mediaStatus, setMediaStatus] = useState({
    damageDone: false,
    damageCount: 0,
    photosDone: false,
    photosCount: 0,
    audioDone: false,
  });
  const [apiInspect, setApiInspect] = useState(
    () => Number(location.state?.inspect) || 0
  );
  const [progressReady, setProgressReady] = useState(false);
  const id = location.state?.id;
  const carName = location.state?.car_name || "Vehicle";
  const report = location.state?.report;

  useEffect(() => {
    if (location.state?.stepsGroup === "checklist" || location.state?.stepsGroup === "media") {
      setGroup(location.state.stepsGroup);
    }
  }, [location.state?.stepsGroup]);

  useEffect(() => {
    let cancelled = false;
    setcarsteploading(true);
    fetchActiveChecklistSections()
      .then((sections) => {
        if (cancelled) return;
        getcardetaol_({ data: sections });
      })
      .finally(() => {
        if (!cancelled) setcarsteploading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setProgressReady(false);

    const loadAnswers = async () => {
      // Seed from navigation state (inspection_cars row from dashboard).
      const fromCarNav = answersMapFromInspectionCar(
        location.state?.inspectionCar
      );
      const fromNav = mergeAnswerMaps(
        fromCarNav,
        answersMapFromReport(location.state?.report)
      );
      const patch = location.state?.checklistAnswersPatch || {};
      if (!cancelled) {
        setSelectedBySection(mergeAnswerMaps(fromNav, patch));
      }

      try {
        const car = await fetchInspectionCarRow(id);
        if (cancelled) return;
        if (car) {
          const fromCars = answersMapFromInspectionCar(car);
          setSelectedBySection(mergeAnswerMaps(fromCars, patch));
          const nextInspect = Number(car.inspect);
          if (Number.isFinite(nextInspect)) {
            setApiInspect(nextInspect);
          }
          history.replace({
            ...location,
            state: {
              ...(location.state || {}),
              id,
              car_name: car.name || carName,
              report: car.report ?? location.state?.report,
              inspect: Number.isFinite(nextInspect)
                ? nextInspect
                : location.state?.inspect,
              inspectionCar: car,
              stepsGroup: location.state?.stepsGroup || group,
              checklistAnswersPatch: undefined,
            },
          });
          return;
        }
      } catch {
        /* fall through to product / selected */
      }

      // Fallback: inspection_product report field (same car payload shape).
      try {
        const product = await fetchProductInspect(id);
        if (cancelled || !product) return;
        const fromProduct = answersMapFromReport(product.report);
        setSelectedBySection(mergeAnswerMaps(fromProduct, patch));
        if (Number.isFinite(Number(product.inspect))) {
          setApiInspect(Number(product.inspect));
        }
        history.replace({
          ...location,
          state: {
            ...(location.state || {}),
            id,
            car_name: product.name || carName,
            report: product.report ?? location.state?.report,
            inspect: product.inspect,
            stepsGroup: location.state?.stepsGroup || group,
            checklistAnswersPatch: undefined,
          },
        });
      } catch {
        /* keep nav/patch answers */
      }
    };

    const loadMedia = async () => {
      const status = await fetchMediaStepStatus(id);
      if (!cancelled) setMediaStatus(status);
    };

    Promise.all([loadAnswers(), loadMedia()]).finally(() => {
      if (!cancelled) setProgressReady(true);
    });

    const onFocus = () => {
      setProgressReady(false);
      Promise.all([loadAnswers(), loadMedia()]).finally(() => {
        if (!cancelled) setProgressReady(true);
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") onFocus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.state?.checklistAnswersPatch]);

  const checklist = Array.isArray(car_detail_data) ? car_detail_data : [];
  const totalSteps = MEDIA_STEPS.length + checklist.length;
  const checklistQuestionTotal = checklist.reduce(
    (sum, item) => sum + (Number(item.questionCount) || 0),
    0
  );
  const checklistAnsweredTotal = checklist.reduce((sum, item) => {
    const answered = sectionAnswers(selectedBySection, item).length;
    const total = Number(item.questionCount) || 0;
    return sum + (total ? Math.min(answered, total) : answered);
  }, 0);
  const checklistLeft = Math.max(
    0,
    checklistQuestionTotal - checklistAnsweredTotal
  );
  const localProgress = computeLocalInspectPercent({
    mediaStatus,
    selectedBySection,
    checklistSections: checklist,
    checklistLength: checklist.length,
    mediaStepCount: MEDIA_STEPS.length,
  });
  // Prefer client % so media (incl. audio) is required for 100%.
  // Until calculation finishes, keep 0 so the bar does not flash a stale value.
  const progress =
    progressReady && !carsteploading
      ? checklist.length > 0
        ? localProgress
        : resolveInspectPercent(apiInspect, localProgress)
      : 0;

  const stepsState = {
    id,
    car_name: carName,
    report,
    inspect: progress,
    stepsGroup: group,
  };

  const goHome = () => {
    history.push({ pathname: "/inspection" });
  };

  const openMediaStep = (path) => {
    history.push({
      pathname: path,
      state: { ...stepsState, stepsGroup: "media" },
    });
  };

  const openChecklistStep = (item) => {
    setcarquestionloading_({ data: true });
    history.push({
      pathname: "/inspection/carupdate",
      state: {
        id: item.id,
        car_id: id,
        car_name: carName,
        step_name: item.title,
        inspect: progress,
        report,
        inspectionCar: location.state?.inspectionCar,
        stepsGroup: "checklist",
        questionCount: item.questionCount,
      },
    });
  };

  const mediaMeta = (step) => {
    if (step.key === "damage") {
      const n = mediaStatus.damageCount || 0;
      return {
        done: n > 0 || mediaStatus.damageDone,
        detail:
          n > 0
            ? `${n} damage point${n === 1 ? "" : "s"} marked`
            : "No damage points yet",
      };
    }
    if (step.key === "photos") {
      const n = mediaStatus.photosCount || 0;
      return {
        done: n > 0 || mediaStatus.photosDone,
        detail:
          n > 0
            ? `${n} photo${n === 1 ? "" : "s"} captured`
            : mediaStatus.photosDone
              ? "Vehicle photos already captured"
              : "No vehicle photos yet",
      };
    }
    if (step.key === "audio") {
      return {
        done: Boolean(mediaStatus.audioDone),
        detail: mediaStatus.audioDone
          ? "Audio note recorded"
          : "No audio recorded yet",
      };
    }
    return { done: false, detail: step.text };
  };

  const loadingBlocks = useMemo(
    () => (
      <div className="pi-steps__loading">
        <ShimmerThumbnail height={72} rounded />
        <ShimmerThumbnail height={72} rounded />
        <ShimmerThumbnail height={72} rounded />
      </div>
    ),
    []
  );

  return (
    <InspectionShell
      badge="Vehicle"
      title={carName}
      onBack={goHome}
      mainClassName="pi-inspection-main--dashboard"
      className="pi-inspection-page--app"
      showFooter
    >
      <div className="pi-steps">
        <header className="pi-steps__hero">
          <div className="pi-steps__hero-top">
            <div className="pi-steps__hero-copy">
              <p className="pi-inspection-label">AutoTrade Port · Inspection</p>
              <h1>{carName}</h1>
              <p className="pi-steps__hero-desktop">
                Full workspace for media capture, damage mapping, and checklist
                sections.
              </p>
              <p className="pi-steps__hero-mobile">
                Complete each step to finish this report.
              </p>
            </div>

            <div className="pi-steps__stats" aria-label="Inspection progress">
              <button
                type="button"
                className={`pi-steps__stat${
                  group === "media" ? " is-active" : ""
                }`}
                onClick={() => setGroup("media")}
              >
                <span>Media</span>
                <strong>{MEDIA_STEPS.length}</strong>
              </button>
              <button
                type="button"
                className={`pi-steps__stat${
                  group === "checklist" ? " is-active" : ""
                }`}
                onClick={() => setGroup("checklist")}
              >
                <span>Checklist</span>
                <strong>{checklist.length}</strong>
              </button>
              <div className="pi-steps__stat">
                <span>Steps</span>
                <strong>{totalSteps || "—"}</strong>
              </div>
              <div className="pi-steps__stat">
                <span>Report</span>
                <strong>{progress}%</strong>
              </div>
            </div>
          </div>

          <div className="pi-steps__progress">
            <div className="pi-steps__progress-label">
              <span>Overall completion</span>
              <em>{progress}%</em>
            </div>
            <div className="pi-steps__track" aria-hidden>
              <span style={{ width: `${progress}%` }} />
            </div>
            <p className="pi-steps__progress-hint">
              {checklistQuestionTotal
                ? `${checklistAnsweredTotal}/${checklistQuestionTotal} checklist · ${
                    [
                      mediaStatus.damageDone,
                      mediaStatus.photosDone,
                      mediaStatus.audioDone,
                    ].filter(Boolean).length
                  }/3 media`
                : "Media steps + checklist questions"}
            </p>
          </div>
        </header>

        <div
          className="pi-steps__tabs"
          role="tablist"
          aria-label="Step groups"
        >
          <button
            type="button"
            role="tab"
            aria-selected={group === "media"}
            className={`pi-steps__tab${group === "media" ? " is-active" : ""}`}
            onClick={() => setGroup("media")}
          >
            Media & damage
            <em>{MEDIA_STEPS.length}</em>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={group === "checklist"}
            className={`pi-steps__tab${
              group === "checklist" ? " is-active" : ""
            }`}
            onClick={() => setGroup("checklist")}
          >
            Checklist
            <em>{checklist.length}</em>
          </button>
        </div>

        {carsteploading ? (
          loadingBlocks
        ) : (
          <div className="pi-steps__layout">
            <aside className="pi-steps__side" aria-label="Step groups">
              <p className="pi-steps__side-label">Workspace</p>
              <button
                type="button"
                className={`pi-steps__side-item${
                  group === "media" ? " is-active" : ""
                }`}
                onClick={() => setGroup("media")}
              >
                <span>Media & damage</span>
                <em>{MEDIA_STEPS.length}</em>
              </button>
              <button
                type="button"
                className={`pi-steps__side-item${
                  group === "checklist" ? " is-active" : ""
                }`}
                onClick={() => setGroup("checklist")}
              >
                <span>Checklist</span>
                <em>{checklist.length}</em>
              </button>
              <button
                type="button"
                className="pi-steps__side-home"
                onClick={goHome}
              >
                Back to hub
              </button>
            </aside>

            <div className="pi-steps__panel">
              {group === "media" ? (
                <section className="pi-steps__section">
                  <div className="pi-steps__section-head">
                    <div>
                      <h2>Media & damage</h2>
                      <p>Photos, damage mapping, and audio notes.</p>
                    </div>
                  </div>
                  <div className="pi-steps__grid">
                    {MEDIA_STEPS.map((step, index) => {
                      const Icon = step.icon;
                      const meta = mediaMeta(step);
                      const countLabel =
                        step.key === "damage"
                          ? mediaStatus.damageCount || 0
                          : step.key === "photos"
                            ? mediaStatus.photosCount || 0
                            : null;
                      return (
                        <button
                          key={step.path}
                          type="button"
                          className={`pi-steps__item pi-steps__item--card${
                            meta.done ? " is-answered" : ""
                          }`}
                          onClick={() => openMediaStep(step.path)}
                        >
                          <span className="pi-steps__item-top">
                            <span className="pi-steps__index">{index + 1}</span>
                            {countLabel != null ? (
                              <span className="pi-steps__count-badge" aria-label={`${countLabel} items`}>
                                {countLabel}
                              </span>
                            ) : null}
                            <span className="pi-steps__icon" aria-hidden>
                              {meta.done ? <HiOutlineCheckCircle /> : <Icon />}
                            </span>
                          </span>
                          <span className="pi-steps__copy">
                            <strong>{step.title}</strong>
                            <em>{meta.detail}</em>
                          </span>
                          <span className="pi-steps__item-cta">
                            {meta.done ? "Review" : "Open"}
                            <BiRightArrowAlt size={18} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : (
                <section className="pi-steps__section">
                  <div className="pi-steps__section-head">
                    <div>
                      <h2>Checklist</h2>
                      <p>
                        {checklist.length
                          ? checklistQuestionTotal
                            ? `${checklistAnsweredTotal} of ${checklistQuestionTotal} answered${
                                checklistLeft
                                  ? ` · ${checklistLeft} left`
                                  : " · all done"
                              }`
                            : `${checklist.length} section${
                                checklist.length === 1 ? "" : "s"
                              } with questions configured.`
                          : "No checklist sections with questions yet."}
                      </p>
                    </div>
                  </div>

                  {checklist.length === 0 ? (
                    <div className="pi-steps__empty">
                      <HiOutlineClipboardList size={28} aria-hidden />
                      <p>Checklist titles will appear here once loaded.</p>
                    </div>
                  ) : (
                    <div className="pi-steps__list">
                      {checklist.map((item, index) => {
                        const answers = sectionAnswers(selectedBySection, item);
                        const totalQ = Number(item.questionCount) || 0;
                        const answered = totalQ
                          ? Math.min(answers.length, totalQ)
                          : answers.length;
                        const left = Math.max(0, totalQ - answered);
                        const complete = totalQ > 0 && answered >= totalQ;
                        const preview = answers.slice(0, 2).join(" · ");
                        return (
                        <button
                          key={item.id || `${item.title}-${index}`}
                          type="button"
                          className={`pi-steps__item${
                            answered ? " is-answered" : ""
                          }${complete ? " is-complete" : ""}`}
                          onClick={() => openChecklistStep(item)}
                        >
                          <span className="pi-steps__index">{index + 1}</span>
                          <span className="pi-steps__icon" aria-hidden>
                            <HiOutlineCheckCircle />
                          </span>
                          <span className="pi-steps__copy">
                            <span className="pi-steps__copy-top">
                              <strong>{item.title}</strong>
                              <span className="pi-steps__item-status">
                                {complete
                                  ? "Done"
                                  : answered
                                    ? "In progress"
                                    : "Start"}
                              </span>
                            </span>
                            <em>
                              {complete
                                ? `Complete · ${answered}/${totalQ} answered`
                                : answered
                                  ? `${answered}/${totalQ || "?"} answered · ${
                                      left || "?"
                                    } left${preview ? ` · ${preview}` : ""}${
                                      answers.length > 2 ? "…" : ""
                                    }`
                                  : totalQ
                                    ? `0/${totalQ} answered · ${totalQ} left`
                                    : "No options selected yet for this section."}
                            </em>
                            {totalQ > 0 ? (
                              <span
                                className="pi-steps__mini-track"
                                aria-hidden
                              >
                                <span
                                  style={{
                                    width: `${Math.round(
                                      (answered / totalQ) * 100
                                    )}%`,
                                  }}
                                />
                              </span>
                            ) : null}
                          </span>
                          <BiRightArrowAlt
                            className="pi-steps__arrow"
                            size={20}
                            aria-hidden
                          />
                        </button>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </InspectionShell>
  );
};

const mapStateToProps = (state) => ({
  car_detail_data: state.cardata.car_detail_data,
  loading: state.cardata.loading,
  carquestionloading: state.cardata.carquestionloading,
});

const mapDispatchToProps = (dispatch) => {
  return {
    getcardetaol_: (response) => dispatch(getcardetaol_(response)),
    setcarquestionloading_: (response) =>
      dispatch(setcarquestionloading(response)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CarSteps);
